import Vue from 'vue';
import dayjs from 'dayjs';
import { axios, blueApi } from '@/axios.config';
import { sortObject } from '@/lib/sorter';
import { mockAdapter } from '@/mocks/axiosMockAdapter.config';
import { cloneDeep } from '@/lib/tools';

/**
 * Hash
 * @Param {string} id - account id, group id, cross tag id
 * @Param {string} level - 'account' or 'acctgroup'
 * @Param {string} vendor - Cloud vendor
 *
 */
export const hash = ({ id, level, vendor }) => {
  return `${id}-${level}-${vendor}`;
};

export const state = {
  budgets: {},
  storedBudget: {
    id: null,
    start_date: null,
    data: [],
  },
  loading: {
    fetch: false,
    post: false,
    put: false,
    delete: false,
  },
  cancelToken: {
    fetch: null,
  },
};

export const formatParams = (data = [], start_date) => {
  data.forEach(i => {
    i.yearMonth = dayjs(i.date).format('YYYYMM');

    if (i.value === '' || i.value === null) i.value = 0;

    i.amount = i.value;

    delete i.date;
    delete i.value;
  });

  return {
    data: {
      fiscalYear: dayjs(start_date).format('YYYY'),
      monthlyBudget: data,
    },
  };
};

export const actions = {
  fetchBudget(
    { state, commit, dispatch, getters, rootState, rootGetters },
    { force = false, year = new Date().getFullYear() }
  ) {
    const methodName = 'fetch';

    if (!force) {
      if (state.budgets[getters.reqHash]) return false;
    }

    // Axios Cancel
    if (state.cancelToken[methodName]) {
      state.cancelToken[methodName].cancel();
    }
    // Axios Cancel - Token
    const cancelToken = new axios.CancelToken.source();
    const token = cancelToken.token;

    // Request hash / URL
    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;
    let reqHash = hash({ id: id, level: level, vendor: vendor });

    const url = `cost/v1/${vendor}/budget/${level}/${id}`;
    let params = {
      year,
    };

    mockAdapter(blueApi, 'get', url, 'getBudget', 200);

    commit('requestStart', { method: methodName, cancelToken: cancelToken });

    return blueApi
      .get(url, { params, cancelToken: token })
      .then(res => {
        commit('updateBudget', { data: res.data, hash: reqHash });
        dispatch('InitializeStoredBudget');
        commit('requestSucceed', { method: methodName });
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          console.log('Request canceled', id);
        } else {
          commit('requestFailed', { method: methodName });
          if (err.response.status === 403) {
            return Promise.resolve('Budget is not set yet');
          }

          if (err.response.status === 404) {
            return Promise.resolve('Budget is not set yet');
          }

          if (err.response.status === 500) {
            return Promise.resolve('Budget is not set yet');
          }

          if (err.response.status === 501) {
            return Promise.resolve('Budget is not set yet');
          }

          throw err;
        }
      });
  },

  InitializeStoredBudget({ commit, getters }) {
    // Budget data
    const targetBudget = getters.getBudget;
    const budget = cloneDeep(targetBudget);

    // Budget ID
    const budgetId = getters.getBudgetId;

    // Budget start date
    let startDate;
    if (!budget.length) {
      startDate = dayjs().format();
    } else {
      startDate = budget[0].date;
    }

    commit('setStoredBudget', {
      id: budgetId,
      data: budget,
      start_date: startDate,
    });
  },

  updateBudget({ state, dispatch }, { year }) {
    if (!state.storedBudget.id) {
      // POST
      return dispatch('postBudget');
    } else {
      // PUT
      return dispatch('putBudget', { year });
    }
  },

  postBudget({ state, commit, rootState, rootGetters }) {
    // Params
    const budgets = cloneDeep(state.storedBudget.data);
    const start = state.storedBudget.start_date;
    const params = formatParams(budgets, start);

    // Url
    const vendor = rootState.vendor;
    const level = rootGetters['usageReports/getLevel'];
    const id = rootState.usageReports.id;
    const url = `cost/v1/${vendor}/budget/${level}/${id}`;

    mockAdapter(blueApi, 'post', url, 'postBudget', 200);

    commit('requestStart', { method: 'post' });

    return blueApi
      .post(url, params)
      .then(() => {
        commit('requestSucceed', { method: 'post' });
      })
      .catch(err => {
        commit('requestFailed', { method: 'post' });
        throw err;
      });
  },

  putBudget({ state, commit, rootState, dispatch }, { year }) {
    // Params
    const budgets = cloneDeep(state.storedBudget.data);
    const start = state.storedBudget.start_date;
    const params = formatParams(budgets, start);

    // Url
    const budgetId = state.storedBudget.id;
    const vendor = rootState.vendor;
    const url = `cost/v1/${vendor}/budget/${budgetId}`;

    mockAdapter(blueApi, 'put', url, 'putBudget', 200);

    commit('requestStart', { method: 'put' });

    return blueApi
      .put(url, params)
      .then(() => {
        commit('requestSucceed', { method: 'put' });
      })
      .then(() => {
        dispatch('fetchBudget', { force: true, year });
      })
      .catch(err => {
        commit('requestFailed', { method: 'put' });
        throw err;
      });
  },

  deleteBudget({ state, commit, rootState }) {
    const budgetId = state.storedBudget.id;
    const vendor = rootState.vendor;

    const url = `cost/v1/${vendor}/budget/${budgetId}`;

    mockAdapter(blueApi, 'delete', url, 'deleteBudget', 200);

    commit('requestStart', { method: 'delete' });

    return blueApi
      .delete(url)
      .then(() => {
        commit('requestSucceed', { method: 'delete' });
      })
      .catch(err => {
        commit('requestFailed', { method: 'delete' });
        throw err;
      });
  },
};

export const mutations = {
  requestStart(state, { method, cancelToken }) {
    state['loading'][method] = true;
    state.cancelToken[method] = cancelToken;
  },

  requestSucceed(state, { method }) {
    state['loading'][method] = false;
    state.cancelToken[method] = null;
  },

  requestFailed(state, { method }) {
    state['loading'][method] = false;
    state.cancelToken[method] = null;
  },

  updateBudget(state, { data, hash }) {
    if (!data.data.monthlyBudget.length) return false;

    // Format date. 2021-01 => 2021-01-01T00:00:00+09:00"
    data.data.monthlyBudget.forEach(i => {
      i.date = dayjs(i.yearMonth).format('YYYY/MM');
      i.value = i.amount;
      delete i.yearMonth;
      delete i.amount;
    });

    data.data.monthlyBudget = sortObject(data.data.monthlyBudget, 'date');

    const budget = {
      id: data.data.id,
      data: data.data.monthlyBudget,
      start_date: data.data.monthlyBudget[0].date,
    };

    Vue.set(state.budgets, hash, budget);
  },

  setStoredBudget(state, { id, data, start_date }) {
    state.storedBudget.id = id;
    state.storedBudget.data = data;
    state.storedBudget.start_date = start_date;
  },

  updateStored(state, { start, term }) {
    const arr = [];

    for (let i = 0; i < term; i++) {
      // Date
      const date = dayjs(start)
        .add(i, 'month')
        .format();

      // Value
      const targetIndex = state.storedBudget.data.findIndex(i => {
        return dayjs(i.date).isSame(date, 'month');
      });
      const value = targetIndex !== -1 ? state.storedBudget.data[targetIndex].value : 0;

      arr.push({
        date: date,
        value: value,
      });
    }

    state.storedBudget.data = arr;
    state.storedBudget.start_date = arr[0].date;
  },

  updateStoredValue(state, { date, value }) {
    const targetIndex = state.storedBudget.data.findIndex(i => dayjs(i.date).isSame(date, 'month'));

    if (targetIndex === -1) return false;

    state.storedBudget.data[targetIndex].value = value;
  },
};

export const getters = {
  reqHash(state, getters, rootState, rootGetters) {
    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;

    return hash({ id: id, level: level, vendor: vendor });
  },

  /**
   * All budget data.
   * @param state
   * @param getters
   * @return {{ date: Date, amount: Number }[]|[]} - e.g. (date: 2021-05)
   */
  getBudget(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.budgets[targetHash] ? state.budgets[targetHash].data : null;

    return data ? data : [];
  },

  /**
   * Budget total of current item
   * @param state
   * @param getters
   * @return {number}
   */
  getBudgetTotal(state, getters) {
    const data = getters.getBudget;

    return data.reduce((acc, cur) => {
      return acc + cur.value;
    }, 0);
  },

  /**
   * Get current budget ID
   * @param state
   * @param getters
   * @return {string|undefined}
   */
  getBudgetId(state, getters) {
    const targetHash = getters.reqHash;
    const id = state.budgets[targetHash] ? state.budgets[targetHash].id : null;

    return id ? id : undefined;
  },

  getStoredBudget(state) {
    const data = state.storedBudget.data;

    return data ? data : [];
  },

  getBudgetTerm(state, getters) {
    const data = getters.getBudget;
    if (!data.length) return [];

    const start = data[0].date;
    const end = data[data.length - 1].date;

    return [start, end];
  },

  getBudgetStartDate(state, getters) {
    const targetHash = getters.reqHash;
    return state.budgets[targetHash] ? state.budgets[targetHash].start_date : null;
  },

  getBudgetForecast(state, getters, rootState, rootGetters) {
    return rootGetters['costForecast/getChartDataMonthlyAll'];
  },
};
